import { useState } from 'react';
import type {
  AnswerOption,
  Answers,
} from '../../domain/entities/answer.entity';
import type { SimuladoQuestion } from '../../domain/entities/question.entity';
import { QuestionCard } from './QuestionCard';
import { QuestionNavigator } from './QuestionNavigator';

interface QuestionStepperProps {
  questions: SimuladoQuestion[];
  answers: Answers;
  /** Grava a resposta da questão (ou `null`, em branco). */
  onAnswer: (number: number, answer: AnswerOption | null) => void;
  onFinish: () => void;
  finishLabel: string;
  /**
   * Abrir na questão 1, e não na primeira em branco — para conferir as
   * respostas recém-fornecidas pela folha.
   */
  startAtFirst?: boolean;
}

/**
 * Uma questão por vez, com o navegador por número — a interação do ensaio.
 *
 * **Clicar na alternativa responde e avança**: a escolha é a resposta, sem
 * botão de confirmar, e a próxima questão aparece em seguida. Na última, a
 * resposta é gravada e a tela fica — dali é finalizar. Clicar de novo na
 * alternativa marcada a desmarca e **não** avança: quem desmarcou quer
 * repensar aquela questão.
 *
 * Só cuida da navegação; onde as respostas moram e o que "finalizar" faz é de
 * quem usa.
 */
export function QuestionStepper({
  questions,
  answers,
  onAnswer,
  onFinish,
  finishLabel,
  startAtFirst = false,
}: QuestionStepperProps) {
  /**
   * A questão na tela. `null` até a pessoa navegar: aí a tela abre na
   * **primeira em branco**, e quem volta depois de um reload cai onde parou,
   * não na questão 1 de novo.
   */
  const [chosenIndex, setChosenIndex] = useState<number | null>(null);
  const firstBlank = questions.findIndex(
    (question) => answers[question.number] === undefined,
  );
  const currentIndex =
    chosenIndex ?? (startAtFirst ? 0 : Math.max(firstBlank, 0));
  const current = questions[currentIndex];

  if (!current) return null;

  const goTo = (index: number) => {
    setChosenIndex(Math.min(Math.max(index, 0), questions.length - 1));
  };

  const handlePick = (answer: AnswerOption) => {
    const isUnmarking = answers[current.number] === answer;
    onAnswer(current.number, isUnmarking ? null : answer);
    if (isUnmarking) return;
    // Fixa a questão atual antes de avançar: com `chosenIndex` ainda nulo, a
    // "primeira em branco" mudaria com a resposta nova e a tela pularia.
    goTo(currentIndex + 1);
  };

  return (
    <div className="qnav-layout">
      <QuestionNavigator
        numbers={questions.map((question) => question.number)}
        answers={answers}
        currentIndex={currentIndex}
        onGoTo={goTo}
      />
      <div className="qnav-main">
        <QuestionCard
          question={current}
          position={{ index: currentIndex, total: questions.length }}
          selected={answers[current.number]}
          onPick={handlePick}
          onPrevious={() => goTo(currentIndex - 1)}
          onNext={() => goTo(currentIndex + 1)}
        />
        {/* Fora do card do navegador e do da questão, do tamanho dos botões
            de navegação: é a ação que fecha o ensaio, e precisa ser achada sem
            procurar. Pode terminar incompleto (branco é resposta legítima, e
            o diagnóstico sabe tratá-lo); só não dá sem nenhuma. */}
        <div className="qnav-finish-row">
          <button
            type="button"
            className="cta qnav-finish"
            onClick={onFinish}
            disabled={Object.keys(answers).length === 0}
          >
            {finishLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
